// src/components/PickingModuleV2.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

function PickingModuleV2() {
  const { user, token } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false); // Para el overlay de carga
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  const [isPickingOrderModalOpen, setIsPickingOrderModalOpen] = useState(false);
  const [selectedPickingOrder, setSelectedPickingOrder] = useState(null);
  
  const [cotizacionFilter, setCotizacionFilter] = useState('sin');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const ordersPerPage = 15;

  const isValidGuideForTransition = (guideNumber) => {
    if (!guideNumber || typeof guideNumber !== 'string') return false;
    const cleanedGuide = guideNumber.trim().toUpperCase();
    return cleanedGuide !== '' && cleanedGuide !== 'N/A' && !/^GUIA-\d+-\d+$/.test(guideNumber) && !cleanedGuide.includes('PENDING') && !cleanedGuide.includes('GENERATING');
  };

  const getPlatformIcon = (canal) => {
    const c = canal?.toUpperCase() || '';
    if (c.includes('MERCADO LIBRE')) return <span className="flex items-center gap-1"><span className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center text-[10px] font-bold">ML</span> Mercado Libre</span>;
    if (c.includes('AMAZON')) return <span className="flex items-center gap-1"><span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">AMZ</span> Amazon</span>;
    if (c.includes('WALMART')) return <span className="flex items-center gap-1"><span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold">WMT</span> Walmart</span>;
    return <span className="flex items-center gap-1"><span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-[10px] font-bold">?</span> {canal}</span>;
  };

  const generatePrintHtml = (order) => { 
    if (!order) return '';
    const today = new Date();
    const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()} ${today.getHours().toString().padStart(2, '0')}:${today.getMinutes().toString().padStart(2, '0')}`;

    const groupedItems = {};
    order.items_a_surtir.forEach(item => {
      if (!groupedItems[item.sku]) {
        groupedItems[item.sku] = { sku: item.sku, descripcion: item.descripcion, total_cantidad: 0, almacenes_surtidos: [] };
      }
      groupedItems[item.sku].total_cantidad += item.cantidad;
      groupedItems[item.sku].almacenes_surtidos.push(`${item.almacen}: ${item.cantidad}pza`);
    });

    let itemsListHtml = Object.values(groupedItems).map(item => `
        <div class="item-entry" style="margin-bottom: 3mm; border-bottom: 0.5px dashed #000; padding-bottom: 1.5mm;">
          <p style="margin: 0; line-height: 1.3;">
            <strong style="font-size: 12pt;">Cant: ${item.total_cantidad}</strong> - 
            <span style="font-size: 11pt;">${item.descripcion} (${item.sku})</span>
          </p>
          <p style="margin: 1mm 0 0 0; font-size: 11pt; font-weight: bold; color: #000;">
            Surte de Alm: ${item.almacenes_surtidos.join(', ')}
          </p>
        </div>
    `).join('');

    let noticesSection = '';
    const hasNotices = (order.notices?.cedis && order.notices.cedis.length > 0) || 
                       (order.notices?.foraneas && order.notices.foraneas.length > 0);

    if (hasNotices) {
      let cedisLines = (order.notices.cedis || []).map(line => `<p style="margin: 1mm 0;">- CEDIS (40): ${line}</p>`).join('');
      let foraneasLines = (order.notices.foraneas || []).map(line => `<p style="margin: 1mm 0;">- Foráneas: ${line}</p>`).join('');
      
      noticesSection = `
        <div style="margin-top: 5mm; border: 2px solid #000; padding: 3mm; font-size: 11pt; line-height: 1.3; background-color: #f9f9f9;">
          <h3 style="font-size: 12pt; font-weight: bold; margin-bottom: 2mm; text-align: center; border-bottom: 1px solid #000; text-transform: uppercase;">Disponibilidad Sugerida</h3>
          ${cedisLines}
          ${foraneasLines}
        </div>
      `;
    }

    const printCount = parseInt(order.picking_print_count, 10) || 0;
    const lastPrintedDate = order.picking_last_printed_at ? new Date(order.picking_last_printed_at).toLocaleString() : 'N/A';
    
    let printStatusMessageHtml = '';
    if (printCount <= 1) {
      printStatusMessageHtml = `
        <div style="font-size: 11pt; font-weight: bold; margin: 3mm 0; text-align: center; border: 1px solid #000; padding: 1.5mm;">
          PRIMERA IMPRESIÓN (Fecha: ${formattedDate})
        </div>
      `;
    } else {
      printStatusMessageHtml = `
        <div style="font-size: 11pt; font-weight: bold; margin: 3mm 0; text-align: center; border: 2.5px solid #000; padding: 1.5mm;">
          REIMPRESIÓN #${printCount} (Última vez: ${lastPrintedDate})
        </div>
      `;
    }

    return `
      <div class="ticket-container" style="font-family: 'Helvetica', 'Arial', sans-serif; width: 80mm; color: #000; line-height: 1.4;">
        <div style="text-align: center; margin-bottom: 6mm;">
          <h1 style="font-size: 20pt; font-weight: bold; margin: 0; border-bottom: 2px solid #000; display: inline-block;">BLUEROVER</h1><br>
          <p style="font-size: 12pt; font-weight: bold; margin: 2mm 0 0 0;">ORDEN DE SURTIDO</p>
          <p style="font-size: 11pt; margin: 1mm 0;">${formattedDate}</p>
          ${printStatusMessageHtml}
        </div>
        <div style="font-size: 12pt; margin-bottom: 4mm;">
          <p style="margin: 1.5mm 0;"><strong>REF:</strong> ${order.referencia_plataforma}</p>
          <p style="margin: 1.5mm 0;"><strong>FOLIO AZ:</strong> ${order.folio_autoazur}</p>
          <p style="margin: 1.5mm 0;"><strong>FOLIO SURTIDO:</strong> <span style="font-size: 14pt; font-weight: 900;">#${order.picking_quote_number || 'N/A'}</span></p>
          <p style="margin: 1.5mm 0;"><strong>ENVÍO:</strong> ${order.metodo_envio || 'N/A'}</p>
        </div>
        <hr style="border: none; border-top: 2px solid #000;">
        <div style="margin-bottom: 3mm;">
          <h3 style="font-size: 12pt; font-weight: bold; margin-bottom: 2mm; text-transform: uppercase; text-decoration: underline;">Detalle de Repisa:</h3>
          ${itemsListHtml}
        </div>
        <hr style="border: none; border-top: 2px solid #000;">
        ${noticesSection}
        <div style="text-align: right; font-weight: bold; margin-top: 5mm; font-size: 14pt;">
          Total Piezas: ${order.items_a_surtir.reduce((s, i) => s + i.cantidad, 0)}
        </div>
        <div style="margin-top: 15mm; text-align: center; font-size: 11pt;">
          <p style="margin-bottom: 8mm;">__________________________</p>
          <p style="font-weight: bold; text-transform: uppercase;">Firma Encargado Almacén</p>
        </div>
      </div>
    `;
  };

  const openPrintDialog = (htmlContent) => {
    const printContainer = document.createElement('div');
    printContainer.innerHTML = htmlContent;
    const styleSheet = document.createElement('style');
    styleSheet.innerText = `@media print { 
      body * { visibility: hidden; } 
      .print-container, .print-container * { visibility: visible; } 
      .print-container { 
        position: absolute; left: 0; top: 0; width: 80mm; padding: 4mm; 
        font-family: 'Helvetica', 'Arial', sans-serif !important;
        font-size: 12pt !important;
        line-height: 1.4 !important;
      } 
    }`;
    printContainer.className = 'print-container';
    document.body.appendChild(printContainer);
    document.head.appendChild(styleSheet);
    window.print();
    setTimeout(() => { document.body.removeChild(printContainer); document.head.removeChild(styleSheet); }, 1000);
  };

  const fetchPickingOrders = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const q = new URLSearchParams({ cotizacionStatus: cotizacionFilter, page: currentPage, limit: ordersPerPage });
      if (fromDate) q.append('fromDate', fromDate);
      if (toDate) q.append('toDate', toDate);
      const res = await fetch(`/api/picking-v2/orders-for-picking?${q.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) { setOrders(data.orders); setTotalOrdersCount(data.totalOrders); setTotalPages(data.totalPages); }
    } catch (err) { setError('Fallo de conexión.'); }
    finally { setLoading(false); }
  }, [token, cotizacionFilter, currentPage, fromDate, toDate]);

  useEffect(() => { setCurrentPage(1); }, [cotizacionFilter, fromDate, toDate]);
  useEffect(() => { fetchPickingOrders(); }, [fetchPickingOrders]);

  const handlePageChange = (p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); };

  const handleGenerate = async (orderId) => {
    setMessage(''); setProcessing(true);
    try {
      const res = await fetch(`/api/picking-v2/generate-picking-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ order_id: orderId }),
      });
      const data = await res.json();
      if (res.ok) { 
        setSelectedPickingOrder(data); 
        setIsPickingOrderModalOpen(true); 
        fetchPickingOrders(); // Refresca lista para que desaparezca
      }
      else { setMessage(`Error: ${data.mensaje}`); }
    } catch (err) { setMessage('Fallo al procesar.'); }
    finally { setProcessing(false); }
  };

  const handleResetQuote = async (orderId) => {
    if (!window.confirm('¿Deseas resetear este surtido? Regresará a la lista de pendientes.')) return;
    setProcessing(true);
    try {
      const res = await fetch(`/api/picking-v2/reset-quote/${orderId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchPickingOrders();
      else alert('No se pudo resetear.');
    } catch (err) { alert('Error de conexión.'); }
    finally { setProcessing(false); }
  };

  const handleRefreshOrder = async (orderId) => {
    setProcessing(true);
    try {
      await fetch(`/api/orders/refresh-status/${orderId}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      fetchPickingOrders();
    } catch (err) { alert('Error al refrescar.'); }
    finally { setProcessing(false); }
  };

  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex flex-col items-center justify-center transition-all duration-300">
      <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center">
        <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-900 font-bold text-lg animate-pulse">Procesando Surtido...</p>
        <p className="text-gray-400 text-sm mt-1">Conectando con Inventario Regional</p>
      </div>
    </div>
  );

  return (
    <div className="p-8 max-w-[1600px] mx-auto min-h-screen bg-gray-50/50">
      {processing && <LoadingOverlay />}
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h2 className="text-4xl font-black text-slate-900 tracking-tight">Surtido Regional</h2>
          <p className="text-slate-500 font-medium mt-1">Gestión avanzada de pedidos con inventario externo</p>
        </div>
        <div className="bg-indigo-600 text-white px-6 py-2 rounded-2xl shadow-lg shadow-indigo-100 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-ping"></span>
            <span className="font-bold text-sm tracking-wide">SISTEMA ONLINE</span>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] shadow-2xl shadow-slate-200 border border-slate-100 overflow-hidden mb-8">
        <div className="p-8 bg-slate-50/80 border-b border-slate-100 flex flex-wrap gap-8 items-center">
          <div className="flex flex-col gap-2">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Filtrar por:</span>
            <div className="flex bg-white rounded-xl shadow-sm p-1 border border-slate-200">
                <button onClick={() => setCotizacionFilter('sin')} className={`px-6 py-2 rounded-lg text-xs font-bold transition-all ${cotizacionFilter === 'sin' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>PENDIENTES</button>
                <button onClick={() => setCotizacionFilter('con')} className={`px-6 py-2 rounded-lg text-xs font-bold transition-all ${cotizacionFilter === 'con' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>SURTIDOS</button>
                <button onClick={() => setCotizacionFilter('todos')} className={`px-6 py-2 rounded-lg text-xs font-bold transition-all ${cotizacionFilter === 'todos' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>TODOS</button>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex flex-col gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">De:</span>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-xl border-slate-200 text-sm font-medium focus:ring-4 focus:ring-indigo-50 shadow-sm" />
            </div>
            <div className="flex flex-col gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">A:</span>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-xl border-slate-200 text-sm font-medium focus:ring-4 focus:ring-indigo-50 shadow-sm" />
            </div>
          </div>
        </div>

        {message && <div className="m-8 p-5 bg-red-50 text-red-700 rounded-3xl border border-red-100 font-bold flex items-center gap-3">
            <span className="w-8 h-8 bg-red-200 rounded-full flex items-center justify-center">!</span> {message}
        </div>}

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50/50">
              <tr>
                <th className="px-8 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Referencia</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Origen</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Venta</th>
                <th className="px-8 py-5 text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Estado Guía</th>
                <th className="px-8 py-5 text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Surtido</th>
                <th className="px-8 py-5 text-right text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map(o => {
                const hasGuide = o.guides.some(g => isValidGuideForTransition(g.guide_number));
                return (
                  <tr key={o.id} className="group hover:bg-slate-50/80 transition-all duration-300">
                    <td className="px-8 py-6">
                      <div className="font-black text-slate-900 text-base">{o.referencia_plataforma}</div>
                      <div className="text-xs text-slate-400 font-bold mt-0.5 tracking-tight">Folio: {o.folio_autoazur}</div>
                    </td>
                    <td className="px-8 py-6">
                        <div className="text-xs font-bold text-slate-600">{getPlatformIcon(o.canal_origen)}</div>
                    </td>
                    <td className="px-8 py-6">
                        <div className="text-sm font-bold text-slate-700">{new Date(o.fecha_venta).toLocaleDateString()}</div>
                    </td>
                    <td className="px-8 py-6 text-center">
                      {hasGuide ? 
                        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-700 border border-emerald-200">GUÍA LISTA</span> : 
                        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-black bg-rose-100 text-rose-700 border border-rose-200">GUÍA PENDIENTE</span>
                      }
                    </td>
                    <td className="px-8 py-6 text-center font-black text-slate-800 text-sm">
                        {o.picking_quote_number ? <span className="text-indigo-600">#{o.picking_quote_number}</span> : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex justify-end gap-3 opacity-80 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleRefreshOrder(o.id)} className="p-2.5 rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 transition-all" title="Refrescar Estado">🔄</button>
                        
                        {o.picking_quote_number ? (
                          <>
                            <button onClick={() => handleGenerate(o.id)} className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-[10px] font-black shadow-lg hover:bg-slate-800 transition-all">REIMPRIMIR</button>
                            <button onClick={() => handleResetQuote(o.id)} className="p-2.5 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all" title="Resetear Surtido">🚮</button>
                          </>
                        ) : (
                          <button 
                            onClick={() => handleGenerate(o.id)} 
                            disabled={!hasGuide}
                            className={`px-6 py-2.5 rounded-xl text-[10px] font-black tracking-widest transition-all shadow-xl ${hasGuide ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:-translate-y-0.5 shadow-indigo-200' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                          >
                            GENERAR SURTIDO
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="p-8 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Página {currentPage} de {totalPages} <span className="ml-2 px-3 py-1 bg-white rounded-full border border-slate-200 text-slate-900">{totalOrdersCount} TOTAL</span></p>
          <div className="flex gap-3">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-6 py-3 rounded-2xl bg-white border border-slate-200 font-bold text-xs text-slate-600 shadow-sm disabled:opacity-50 hover:bg-slate-50">Anterior</button>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-6 py-3 rounded-2xl bg-white border border-slate-200 font-bold text-xs text-slate-600 shadow-sm disabled:opacity-50 hover:bg-slate-50">Siguiente</button>
          </div>
        </div>
      </div>

      {isPickingOrderModalOpen && selectedPickingOrder && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[110] p-6" onClick={() => setIsPickingOrderModalOpen(false)}>
            <div className="bg-white p-10 rounded-[3rem] shadow-2xl max-w-xl w-full border border-white/20 relative overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500"></div>
                
                <h2 className="text-3xl font-black text-slate-900 mb-2">¡Surtido Generado!</h2>
                <p className="text-slate-500 font-medium mb-8">La orden ha sido registrada correctamente en el sistema regional.</p>
                
                <div className="space-y-4 mb-10">
                    <div className="flex justify-between items-center bg-slate-50 p-6 rounded-3xl border border-slate-100">
                        <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Folio Surtido</span>
                        <span className="text-2xl font-black text-indigo-600 tracking-tighter">#{selectedPickingOrder.picking_quote_number}</span>
                    </div>
                    
                    {/* AVISOS CONDICIONALES EN EL MODAL */}
                    {selectedPickingOrder.notices?.cedis?.length > 0 && (
                        <div className="bg-amber-50 border-2 border-amber-200 p-6 rounded-3xl flex items-start gap-4">
                            <span className="text-2xl mt-1">🟠</span>
                            <div>
                                <h4 className="font-black text-amber-900 text-xs uppercase tracking-widest mb-1">Stock en CEDIS (40)</h4>
                                <p className="text-amber-700 text-sm font-bold leading-relaxed">{selectedPickingOrder.notices.cedis.join(', ')}</p>
                            </div>
                        </div>
                    )}
                    {selectedPickingOrder.notices?.foraneas?.length > 0 && (
                        <div className="bg-blue-50 border-2 border-blue-200 p-6 rounded-3xl flex items-start gap-4">
                            <span className="text-2xl mt-1">🔵</span>
                            <div>
                                <h4 className="font-black text-blue-900 text-xs uppercase tracking-widest mb-1">Stock en Foráneas</h4>
                                <p className="text-blue-700 text-sm font-bold leading-relaxed">{selectedPickingOrder.notices.foraneas.join(', ')}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 gap-4">
                    <button onClick={() => openPrintDialog(generatePrintHtml(selectedPickingOrder))} className="w-full bg-slate-900 text-white py-5 rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-slate-300 hover:bg-slate-800 transition-all hover:scale-[1.02]">IMPRIMIR TICKET DE ALMACÉN</button>
                    <button onClick={() => setIsPickingOrderModalOpen(false)} className="w-full py-4 text-slate-400 font-black text-[10px] uppercase tracking-widest hover:text-slate-600 transition-colors">Cerrar Ventana</button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}

export default PickingModuleV2;
